import type { CurrentUser, Permission } from "@spf/contracts";

export type ViewId =
  | "dashboard"
  | "live"
  | "battery"
  | "solar"
  | "analytics"
  | "telemetry"
  | "site"
  | "settings"
  | "sessions"
  | "audit";

export interface ViewDefinition {
  id: ViewId;
  /**
   * The navigation label. It is also the page's heading, and that is a
   * contract rather than a coincidence: the browser suite clicks a nav item by
   * name and then waits for a heading of the same name
   * (`apps/web/e2e/accessibility.spec.ts`, `openSection`).
   */
  label: string;
  /**
   * The URL. Added in slice 5B: navigation used to be component state, so
   * nothing was linkable, the back button did nothing and a refresh always
   * returned to Live. `installNotFound` in `apps/api/src/errors.ts` already
   * serves the shell for any non-API GET that accepts HTML, so these work
   * server-side with no API change.
   */
  path: string;
  /** The permission that puts this view in the navigation. */
  permission: Permission;
  /**
   * Where the link is drawn.
   *
   * `primary` is the design's centre pill rail, which holds five links and is
   * built for five. `account` is everything that belongs to the person rather
   * than to the installation -- their sessions, the activity log, how the
   * instance is configured -- and those hang off the avatar, where the design
   * already puts a menu. Ten pills in the rail is what made the header wrap
   * into a second row and stop looking like the design at all.
   */
  group: "primary" | "account";
}

/**
 * Navigation is derived from the caller's permissions, not from their role
 * name, for the same reason the server checks permissions: a role is a bundle
 * that can change, and the question a view actually asks is "may this person
 * read the audit log", not "are they an admin".
 *
 * Hiding a view is presentation only. Every endpoint behind these still
 * enforces its own permission, so a hidden view is inaccessible, not merely
 * invisible.
 */
export const VIEWS: readonly ViewDefinition[] = [
  // The rail's order is the design's own: Dashboard, Solar, Battery,
  // Analytics, Live. It is not the order the app opens in -- `LANDING` below
  // decides that, and it is Live.
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    permission: "site:read",
    group: "primary",
  },
  { id: "solar", label: "Solar", path: "/solar", permission: "site:read", group: "primary" },
  { id: "battery", label: "Battery", path: "/battery", permission: "site:read", group: "primary" },
  {
    id: "analytics",
    label: "Analytics",
    path: "/analytics",
    permission: "site:read",
    group: "primary",
  },
  { id: "live", label: "Live", path: "/live", permission: "site:read", group: "primary" },
  {
    id: "telemetry",
    label: "Telemetry",
    path: "/telemetry",
    permission: "site:read",
    group: "account",
  },
  { id: "site", label: "Site", path: "/site", permission: "site:read", group: "account" },
  // `settings:read`, not `settings:write`. A viewer holds the read permission
  // and may see how the instance is configured; the page renders read-only for
  // them, because the write is what admin gates (docs/REQUIREMENTS.md section
  // 2, where the instance tier is changed by the admin role).
  {
    id: "settings",
    label: "Settings",
    path: "/settings",
    permission: "settings:read",
    group: "account",
  },
  {
    id: "sessions",
    label: "Sessions",
    path: "/sessions",
    permission: "sessions:read:self",
    group: "account",
  },
  { id: "audit", label: "Activity", path: "/activity", permission: "audit:read", group: "account" },
];

export function viewsFor(user: CurrentUser): ViewDefinition[] {
  return VIEWS.filter((view) => user.permissions.includes(view.permission));
}

/**
 * Which view a URL selects, or null for one this app does not have.
 *
 * Null rather than a default, because the caller has to tell "this path is not
 * a view" from "this path is the first view" -- an unknown path is redirected
 * to the landing view and the URL corrected, and silently rendering the
 * dashboard at `/nonsense` would leave a wrong URL in the address bar.
 */
export function viewForPath(path: string): ViewDefinition | null {
  const trimmed = path.replace(/\/+$/, "");
  if (trimmed === "") return null;
  return VIEWS.find((view) => view.path === trimmed) ?? null;
}

/**
 * Where signing in lands.
 *
 * Live rather than the dashboard, and that is a decision on record rather than
 * an ordering accident: "see what the system is doing right now" is the first
 * of the jobs in docs/REQUIREMENTS.md section 1, and the operator chose this
 * screen explicitly (docs/STATE.md, operator action 10). The browser suite
 * waits for the "Live" heading as its proof that sign-in worked, so moving it
 * is a deliberate change to that contract and not a tidy-up.
 *
 * Held separately from the array order so the navigation can lead with
 * Dashboard without changing where the app opens.
 */
export const LANDING: ViewId = "live";

/** The landing view if the caller may see it, else the first they may. */
export function landingFor(available: readonly ViewDefinition[]): ViewDefinition | undefined {
  return available.find((view) => view.id === LANDING) ?? available[0];
}
